import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Plus, QrCode, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Dropdown, SegmentedControl } from '@/components/ui/select'
import { InlineBarcode } from '@/components/ui/inline-barcode'
import { apiClient } from '@/lib/api-client'
import { compactBarcode } from '@/lib/barcode'
import { toast } from 'react-hot-toast'

interface Device {
  id: number
  name: string
  description?: string
  deviceType: 'chargeable' | 'non-chargeable' | 'plugin' | 'single-use'
  pluginType?: 'wall_socket' | 'usb' | null
  chargerAvailable?: boolean
  chargerId?: string
  batteryType?: 'AAA' | 'AA'
  batteryCount?: number
  status: string
  ownershipType?: 'company' | 'project'
  projectId?: number
  deviceModelId?: number
  serialNumber?: string
}

interface Project {
  id: number
  name: string
}

interface DeviceModelOption {
  id: number
  name: string
  brand?: string
  modelNumber?: string
  deviceType: Device['deviceType']
  pluginType?: Device['pluginType']
  chargerAvailable?: boolean
  batteryType?: Device['batteryType']
  batteryCount?: number
  description?: string
}

interface DeviceFormProps {
  isOpen: boolean
  onClose: () => void
  device?: Device | null
  defaultModelId?: number
  onSuccess: () => void
}

interface FormData {
  mode: 'existing' | 'new'
  deviceModelId?: number
  modelName: string
  brand?: string
  modelNumber?: string
  description?: string
  deviceType: Device['deviceType']
  pluginType?: 'wall_socket' | 'usb'
  chargerAvailable: boolean
  chargerId?: string
  batteryType?: 'AAA' | 'AA'
  batteryCount?: number
  ownershipType: 'company' | 'project'
  projectId?: number
  status: string
  serialNumber?: string
  name?: string
}

interface AssetRow {
  key: string
  serial: string
  chargerId: string
}

function newRow(serial = '', chargerId = ''): AssetRow {
  return { key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, serial, chargerId }
}

export function DeviceForm({ isOpen, onClose, device, defaultModelId, onSuccess }: DeviceFormProps) {
  const [projects, setProjects] = useState<Project[]>([])
  const [models, setModels] = useState<DeviceModelOption[]>([])
  const [loading, setLoading] = useState(false)
  const [assets, setAssets] = useState<AssetRow[]>([newRow()])
  const [cameraOn, setCameraOn] = useState(false)
  const [scanTarget, setScanTarget] = useState<string | null>(null)
  const [pulse, setPulse] = useState<{ key: string; kind: 'added' | 'duplicate' } | null>(null)
  const assetsRef = useRef(assets)
  assetsRef.current = assets

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      mode: 'new',
      deviceType: 'non-chargeable',
      chargerAvailable: false,
      ownershipType: 'company',
      status: 'available',
    },
  })

  const mode = watch('mode')
  const deviceType = watch('deviceType')
  const ownershipType = watch('ownershipType')
  const chargerAvailable = watch('chargerAvailable')
  const selectedModelId = watch('deviceModelId')
  const serialNumber = watch('serialNumber')
  const modelLocked = mode === 'existing' && !device

  useEffect(() => {
    if (!isOpen) return

    const load = async () => {
      try {
        const [projectsRes, modelsRes] = await Promise.all([
          apiClient.get('/projects'),
          apiClient.get('/device-models'),
        ])
        setProjects(projectsRes.data)
        setModels(modelsRes.data)
      } catch {
        toast.error('Failed to load form data')
      }
    }

    load()
    setCameraOn(false)
    setScanTarget(null)
    setAssets([newRow()])

    if (device) {
      reset({
        mode: device.deviceModelId ? 'existing' : 'new',
        deviceModelId: device.deviceModelId,
        modelName: device.name,
        description: device.description || '',
        deviceType: device.deviceType,
        pluginType: device.pluginType || undefined,
        chargerAvailable: !!device.chargerAvailable,
        chargerId: device.chargerId || '',
        batteryType: device.batteryType || undefined,
        batteryCount: device.batteryCount || undefined,
        ownershipType: device.ownershipType || (device.projectId ? 'project' : 'company'),
        projectId: device.projectId || undefined,
        status: device.status,
        serialNumber: device.serialNumber || '',
        name: device.name,
      })
    } else {
      reset({
        mode: defaultModelId ? 'existing' : 'new',
        deviceModelId: defaultModelId,
        modelName: '',
        brand: '',
        modelNumber: '',
        description: '',
        deviceType: 'non-chargeable',
        pluginType: undefined,
        chargerAvailable: false,
        chargerId: '',
        batteryType: undefined,
        batteryCount: undefined,
        ownershipType: 'company',
        projectId: undefined,
        status: 'available',
        serialNumber: '',
      })
    }
  }, [isOpen, device, defaultModelId, reset])

  useEffect(() => {
    if (!selectedModelId || mode !== 'existing') return
    const selected = models.find((item) => item.id === Number(selectedModelId))
    if (!selected) return
    setValue('modelName', selected.name)
    setValue('brand', selected.brand || '')
    setValue('modelNumber', selected.modelNumber || '')
    setValue('description', selected.description || '')
    setValue('deviceType', selected.deviceType)
    setValue('pluginType', selected.pluginType || undefined)
    setValue('chargerAvailable', !!selected.chargerAvailable)
    setValue('batteryType', selected.batteryType || undefined)
    setValue('batteryCount', selected.batteryCount || undefined)
  }, [selectedModelId, mode, models, setValue])

  const markRow = (key: string, kind: 'added' | 'duplicate') => {
    setPulse(null)
    window.requestAnimationFrame(() => {
      setPulse({ key, kind })
      window.requestAnimationFrame(() => {
        document.getElementById(`asset-row-${key}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    })
  }

  const applySerial = (code: string) => {
    const compact = compactBarcode(code)
    if (!compact) return

    if (device) {
      setValue('serialNumber', code, { shouldDirty: true })
      setCameraOn(false)
      return
    }

    const current = assetsRef.current
    const existing = current.find(
      (row) => row.serial.trim() && compactBarcode(row.serial) === compact && row.key !== scanTarget,
    )
    if (existing) {
      markRow(existing.key, 'duplicate')
      setScanTarget(null)
      return
    }

    let next: AssetRow[]
    let key: string
    if (scanTarget) {
      key = scanTarget
      next = current.map((row) => (row.key === scanTarget ? { ...row, serial: code } : row))
    } else {
      const emptyIndex = current.findIndex((row) => !row.serial.trim())
      if (emptyIndex >= 0) {
        key = current[emptyIndex].key
        next = current.slice()
        next[emptyIndex] = { ...next[emptyIndex], serial: code }
      } else {
        const row = newRow(code)
        key = row.key
        next = [...current, row]
      }
    }

    assetsRef.current = next
    setAssets(next)
    setScanTarget(null)
    markRow(key, 'added')
  }

  const onSubmit = async (data: FormData) => {
    const needsCharger = data.deviceType === 'chargeable' && data.chargerAvailable
    const prepared = assets
      .map((row) => ({
        serialNumber: row.serial.trim(),
        chargerId: row.chargerId.trim(),
      }))
      .filter((row) => row.serialNumber || row.chargerId)
    if (!device && prepared.length === 0) {
      toast.error('Add at least one serial number')
      return
    }
    if (!device && prepared.some((row) => !row.serialNumber)) {
      toast.error('Each asset needs a serial number')
      return
    }
    if (!device && needsCharger && prepared.some((row) => !row.chargerId)) {
      toast.error('Enter a charger ID for each asset')
      return
    }

    setLoading(true)
    try {
      if (device) {
        await apiClient.put(`/devices/${device.id}`, {
          name: data.name || data.modelName,
          description: data.description,
          deviceModelId: data.deviceModelId,
          deviceType: data.deviceType,
          pluginType: data.pluginType,
          chargerAvailable: data.chargerAvailable,
          chargerId: data.deviceType === 'chargeable' && data.chargerAvailable ? data.chargerId : null,
          batteryType: data.batteryType,
          batteryCount: data.batteryCount,
          ownershipType: data.ownershipType,
          projectId: data.projectId,
          status: device.status === 'assigned' || device.status === 'assigned_pending'
            ? undefined
            : data.status,
          serialNumber: data.serialNumber,
        })
        toast.success('Asset updated')
      } else {
        await apiClient.post('/devices/bulk', {
          deviceModelId: data.mode === 'existing' ? Number(data.deviceModelId) : undefined,
          modelName: data.modelName,
          brand: data.brand,
          modelNumber: data.modelNumber,
          description: data.description,
          deviceType: data.deviceType,
          pluginType: data.pluginType,
          chargerAvailable: data.chargerAvailable,
          batteryType: data.batteryType,
          batteryCount: data.batteryCount,
          ownershipType: data.ownershipType,
          projectId: data.projectId,
          status: data.status,
          assets: prepared.map((row) => ({
            serialNumber: row.serialNumber,
            chargerAvailable: data.chargerAvailable,
            chargerId: needsCharger ? row.chargerId : undefined,
          })),
        })
        toast.success(prepared.length === 1 ? 'Asset created' : `${prepared.length} assets created`)
      }
      onSuccess()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save device')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={device ? 'Edit asset' : defaultModelId ? 'Add assets' : 'Add device model & assets'}
      size="xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {!device && (
          <SegmentedControl
            value={mode}
            onChange={(value) => setValue('mode', value as FormData['mode'])}
            options={[
              { value: 'new', label: 'New model' },
              { value: 'existing', label: 'Existing model' },
            ]}
          />
        )}

        {!device && mode === 'existing' && (
          <Field label="Device model">
            <Controller
              name="deviceModelId"
              control={control}
              rules={{ required: 'Select a model' }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ? String(field.value) : ''}
                  placeholder="Select a model"
                  onChange={(value) => field.onChange(Number(value))}
                  options={models.map((item) => ({
                    value: String(item.id),
                    label: `${item.brand ? `${item.brand} · ` : ''}${item.name}`,
                  }))}
                />
              )}
            />
            {errors.deviceModelId && <p className="text-sm text-destructive">Select a model</p>}
          </Field>
        )}

        {!device && mode === 'new' && (
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Model name" className="md:col-span-2">
              <Input
                {...register('modelName', { required: 'Model name is required' })}
                placeholder="HP EliteBook 840"
              />
              {errors.modelName && <p className="text-sm text-destructive">Model name is required</p>}
            </Field>
            <Field label="Brand">
              <Input {...register('brand')} placeholder="HP" />
            </Field>
            <Field label="Model number">
              <Input {...register('modelNumber')} placeholder="840 G9" />
            </Field>
          </div>
        )}

        {device && (
          <Field label="Asset name">
            <Input {...register('name', { required: true })} />
          </Field>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Power type">
            <Controller
              name="deviceType"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  disabled={modelLocked}
                  onChange={(value) => field.onChange(value)}
                  options={[
                    { value: 'chargeable', label: 'Chargeable' },
                    { value: 'non-chargeable', label: 'Non-chargeable' },
                    { value: 'plugin', label: 'Plugin' },
                    { value: 'single-use', label: 'Single use' },
                  ]}
                />
              )}
            />
          </Field>
          <Field label="Ownership">
            <Controller
              name="ownershipType"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  options={[
                    { value: 'company', label: 'Company asset' },
                    { value: 'project', label: 'Project asset' },
                  ]}
                />
              )}
            />
          </Field>
          <Field label="Status">
            {device && (device.status === 'assigned' || device.status === 'assigned_pending') ? (
              <div className="flex h-10 items-center rounded-lg border border-input bg-muted/40 px-3 text-sm">
                {device.status === 'assigned_pending' ? 'Pending approval' : 'Assigned'}
              </div>
            ) : (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    value={['available', 'unavailable', 'broken'].includes(field.value) ? field.value : 'available'}
                    onChange={(value) => field.onChange(value)}
                    options={[
                      { value: 'available', label: 'Available' },
                      { value: 'unavailable', label: 'Unavailable' },
                      { value: 'broken', label: 'Broken' },
                    ]}
                  />
                )}
              />
            )}
          </Field>
        </div>

        {ownershipType === 'project' && (
          <Field label="Project">
            <Controller
              name="projectId"
              control={control}
              rules={{ required: 'Select a project' }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ? String(field.value) : ''}
                  placeholder="Select project"
                  onChange={(value) => field.onChange(value ? Number(value) : undefined)}
                  options={projects.map((project) => ({
                    value: String(project.id),
                    label: project.name,
                  }))}
                />
              )}
            />
            {errors.projectId && <p className="text-sm text-destructive">Select a project</p>}
          </Field>
        )}

        {deviceType === 'plugin' && (
          <Field label="Plugin connection">
            <Controller
              name="pluginType"
              control={control}
              rules={{ required: 'Select a connection' }}
              render={({ field }) => (
                <Dropdown
                  value={field.value || ''}
                  placeholder="Select connection"
                  disabled={modelLocked}
                  onChange={(value) => field.onChange(value)}
                  options={[
                    { value: 'wall_socket', label: 'Wall socket' },
                    { value: 'usb', label: 'USB' },
                  ]}
                />
              )}
            />
          </Field>
        )}

        {deviceType === 'chargeable' && (
          <Controller
            name="chargerAvailable"
            control={control}
            render={({ field }) => (
              <button
                type="button"
                role="switch"
                aria-checked={!!field.value}
                onClick={() => field.onChange(!field.value)}
                className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3 text-left text-sm"
              >
                <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${field.value ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
                  <span
                    className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${field.value ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </span>
                <span>
                  <span className="block">Charger included</span>
                  <span className="block text-xs text-muted-foreground">
                    When a charger is included, each asset gets its own charger ID.
                  </span>
                </span>
              </button>
            )}
          />
        )}

        {deviceType === 'single-use' && (
          <p className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground">
            Inbuilt battery. This product is not rechargeable. Mark the asset EoL when it is finished.
          </p>
        )}

        {deviceType === 'non-chargeable' && (
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Battery type">
              <Controller
                name="batteryType"
                control={control}
                rules={{ required: 'Select a battery type' }}
                render={({ field }) => (
                  <Dropdown
                    value={field.value || ''}
                    placeholder="Select type"
                    disabled={modelLocked}
                    onChange={(value) => field.onChange(value)}
                    options={[
                      { value: 'AAA', label: 'AAA' },
                      { value: 'AA', label: 'AA' },
                    ]}
                  />
                )}
              />
            </Field>
            <Field label="Battery count">
              <Input
                type="number"
                min={1}
                max={10}
                disabled={modelLocked}
                {...register('batteryCount', {
                  required: deviceType === 'non-chargeable',
                  valueAsNumber: true,
                })}
              />
            </Field>
          </div>
        )}

        <div className="space-y-3 rounded-lg border border-border">
          <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2.5">
            <div>
              <p className="text-sm font-medium">{device ? 'This asset' : 'Assets'}</p>
              {!device && deviceType === 'chargeable' && chargerAvailable && (
                <p className="text-xs text-muted-foreground">Add the charger ID on the same row as each serial number.</p>
              )}
            </div>
            <Button
              type="button"
              variant={cameraOn ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setScanTarget(null)
                setCameraOn((current) => !current)
              }}
            >
              <QrCode className="mr-2 h-4 w-4" />
              {cameraOn ? 'Stop' : 'Scan'}
            </Button>
          </div>

          {cameraOn && (
            <div className="px-3">
              <InlineBarcode onDetect={applySerial} />
            </div>
          )}

          {device ? (
            <div className="grid gap-3 px-3 pb-3 md:grid-cols-2">
              <Field label="Serial number">
                <Input
                  value={serialNumber || ''}
                  placeholder="Serial number"
                  onChange={(event) => setValue('serialNumber', event.target.value, { shouldDirty: true })}
                />
              </Field>
              {deviceType === 'chargeable' && chargerAvailable && (
                <Field label="Charger ID">
                  <Input
                    {...register('chargerId', {
                      required: 'Charger ID is required when a charger is included',
                    })}
                    placeholder="CHG-1024"
                  />
                  {errors.chargerId && (
                    <p className="text-sm text-destructive">{errors.chargerId.message}</p>
                  )}
                </Field>
              )}
            </div>
          ) : (
            <div>
              {assets.map((row, index) => (
                <div
                  key={row.key}
                  id={`asset-row-${row.key}`}
                  className={`flex items-center gap-2 border-b border-border/70 px-3 py-2 last:border-b-0 ${
                    pulse?.key === row.key
                      ? pulse.kind === 'added'
                        ? 'asset-row-added'
                        : 'asset-row-duplicate'
                      : ''
                  }`}
                >
                  <span className="w-5 text-center text-xs tabular-nums text-muted-foreground">{index + 1}</span>
                  <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
                    <Input
                      value={row.serial}
                      placeholder="Serial number"
                      className="h-9"
                      onChange={(event) => {
                        const serial = event.target.value
                        const compact = compactBarcode(serial)
                        const existing = assetsRef.current.find((item) => (
                          item.key !== row.key && item.serial.trim() && compact && compactBarcode(item.serial) === compact
                        ))
                        if (existing) {
                          markRow(existing.key, 'duplicate')
                          return
                        }
                        const next = assetsRef.current.map((item) => (
                          item.key === row.key ? { ...item, serial } : item
                        ))
                        assetsRef.current = next
                        setAssets(next)
                      }}
                    />
                    {deviceType === 'chargeable' && chargerAvailable && (
                      <Input
                        value={row.chargerId}
                        placeholder="Charger ID"
                        className="h-9 sm:w-40"
                        onChange={(event) => {
                          const chargerId = event.target.value
                          const next = assetsRef.current.map((item) => (
                            item.key === row.key ? { ...item, chargerId } : item
                          ))
                          assetsRef.current = next
                          setAssets(next)
                        }}
                      />
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label="Scan this asset"
                    onClick={() => {
                      setScanTarget(row.key)
                      setCameraOn(true)
                    }}
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label="Remove asset"
                    disabled={assets.length === 1}
                    onClick={() => setAssets((current) => current.filter((item) => item.key !== row.key))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="px-3 py-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setAssets((current) => [...current, newRow()])}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add asset
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : device ? 'Update asset' : 'Create assets'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function Field({
  label,
  children,
  className,
}: {
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={className ? `space-y-1.5 ${className}` : 'space-y-1.5'}>
      <label className="block text-sm font-medium">{label}</label>
      {children}
    </div>
  )
}
